import { Component, OnInit } from '@angular/core';
import {
  IComponentHasChanges,
  UserService,
  CampusWard,
  CampusWardsDataService,
  WardGroupType,
  WardGroup,
  SortService
} from '../../shared';
import { LeaderComponent } from '../leader.component';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './ward-groups.component.html',
  styleUrls: ['./ward-groups.component.scss']
})
export class WardGroupsComponent extends LeaderComponent
  implements OnInit, IComponentHasChanges {
  loading = false;
  hasLoadingError = false;

  ward: CampusWard;
  groupTypes: WardGroupType[];

  actionTitle = '';
  groupForm: FormGroup;
  saving = false;
  saved = false;

  _selectedGroup: WardGroup;
  get selectedGroup(): WardGroup {
    return this._selectedGroup;
  }
  set selectedGroup(group: WardGroup) {
    this.saved = false;
    this._selectedGroup = group;

    if (this._selectedGroup) {
      if (this.isEditing()) {
        this.actionTitle = `Update Information: ${this._selectedGroup.name}`;
      } else {
        this.actionTitle = 'Add a New Group';
      }

      this.groupForm.reset();
      this.groupForm.patchValue(this._selectedGroup);
    }
  }

  /**
   * Gets the unique group types that the ward is using right now.
   */
  get usedGroupTypes(): WardGroupType[] {
    const uniqueTypes: WardGroupType[] = [];
    if (this.groups) {
      this.groups.forEach(group => {
        if (
          uniqueTypes.find(
            type =>
              type.campusWardGroupTypeId === group.type.campusWardGroupTypeId
          ) === undefined
        ) {
          uniqueTypes.push(group.type);
        }
      });
      uniqueTypes.sort(this.sorter.sortWardGroupTypesAsc);
    }

    return uniqueTypes;
  }

  private groups: WardGroup[];

  constructor(
    protected userService: UserService,
    protected dialog: MatDialog,
    private formBuilder: FormBuilder,
    private wardService: CampusWardsDataService,
    private sorter: SortService
  ) {
    super(userService, dialog);
  }

  ngOnInit() {
    super.ngOnInit();
    this.groupForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      type: [null, [Validators.required]],
      active: [true]
    });

    this.loadGroupTypes();

    // TODO if not proxied, load the ward details that the leader is in charge of
    if (this.isWardLeader()) {
      this.loadWardGroups(this.getWard());
    }
  }

  /**
   * Indicates whether or not there are unsaved changes on a group.
   */
  hasUnsavedChanges(): boolean {
    return this.groupForm.dirty;
  }

  /**
   * Gets a message letting the user know where their unsaved changes are.
   */
  get unsavedMessage(): string {
    if (this.isEditing()) {
      return `You have unsaved changes to the group: ${this.selectedGroup.name
        }`;
    } else {
      return 'You have not saved the new group.';
    }
  }

  /**
   * Update the ward that we're working with when the admin
   */
  onWardSelected(ward: CampusWard) {
    if (ward) {
      this.loadWardGroups(ward);
    }
  }

  /**
   * Gets the wards current groups of this type.
   */
  getWardsGroups(type: WardGroupType): WardGroup[] {
    if (!this.groups) {
      return [];
    }

    const groups = this.groups.filter(
      g => g.type.campusWardGroupTypeId === type.campusWardGroupTypeId
    );
    groups.sort(this.sorter.sortWardGroupsAsc);

    return groups;
  }

  /**
   * Creates a new empty ward group and selects it for editing.
   */
  createNewWardGroup() {
    this.selectedGroup = new WardGroup(this.ward.campusWardId);
  }

  /**
   * Unselects the current group.
   */
  cancel() {
    this.selectedGroup = null;
  }

  /**
   * Creates or updates the selected ward group.
   */
  save() {
    if (this.saving || !this.groupForm.dirty || !this.groupForm.valid) {
      return;
    }

    // get our data from the form
    const group = Object.assign({}, this.selectedGroup, this.groupForm.value);

    this.saved = false; // reset this flag

    this.saving = true;
    if (this.isEditing()) {
      this.wardService
        .updateWardGroup(group)
        .pipe(
          finalize(() => (this.saving = false))
        )
        .subscribe(
          updatedGroup => {
            const group = <WardGroup>updatedGroup;
            // replace it in our dropdown in case they changed the name
            const index = this.groups.findIndex(
              w => w.campusWardGroupId === group.campusWardGroupId
            );
            this.groups.splice(index, 1);
            this.groups.push(group);

            // select it
            this.selectedGroup = group;
            this.saved = true;
          },
          error => {
            this.showError(error);
          }
        );
    } else {
      this.wardService
        .createWardGroup(group)
        .pipe(
          finalize(() => (this.saving = false))
        )
        .subscribe(
          newGroup => {
            const group = <WardGroup>newGroup;
            // set it, add it to our dropdown, select it in the dropdown so we're in edit mode
            this.groups.push(group);
            this.selectedGroup = group;
            this.saved = true;
          },
          error => {
            this.showError(error);
          }
        );
    }
  }

  /**
   * Function for helping us compare group types so our <select> gets it correct.
   */
  compareGroupTypes(type1: WardGroupType, type2: WardGroupType) {
    return type1 && type2
      ? type1.campusWardGroupTypeId === type2.campusWardGroupTypeId
      : type1 === type2;
  }

  /**
   * Whether or not we're editing or adding a group.
   */
  private isEditing() {
    return this.selectedGroup.campusWardGroupId > 0;
  }

  /**
   * Handles the ward's groups coming back and gets things ready to work with them.
   */
  private handleGroups(groups: WardGroup[]) {
    this.groups = groups;

    // TODO sort, group?
  }

  /**
   * Load the selected ward's groups so the user can modify or create new ones.
   */
  private loadWardGroups(ward: CampusWard) {
    this.ward = ward;
    if (!this.ward.isForMarriedStudents) {
      this.saved = false;

      this.loading = true;
      this.wardService
        .getWardGroups(this.ward.campusWardId)
        .pipe(
          finalize(() => (this.loading = false))
        )
        .subscribe(groups => {
          this.handleGroups(groups);
        });
    }
  }

  /**
   * Loads the valid ward group types.
   */
  private loadGroupTypes() {
    if (!this.loading) {
      this.loading = true;
      this.wardService
        .getWardGroupTypes()
        .pipe(
          finalize(() => (this.loading = false))
        )
        .subscribe(
          data => {
            this.groupTypes = data;
            this.groupTypes.sort(this.sorter.sortWardGroupTypesAsc);
          },
          error => {
            this.hasLoadingError = true;
          }
        );
    }
  }
}
